/**
 * Curated list of athlete-facing lab flags (PH2-8 W7 ScreenLab).
 *
 * Frontend-side catalogue — the backend whitelists the same set
 * (`api/routers/athlete_profile.py:_ALLOWED_LAB_FLAGS`). Keep both
 * in sync when adding a new experiment.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type LabFlagKey =
  | "advanced_analytics_preview"
  | "experimental_charts"
  | "voice_input_chat"
  | "show_beta_badges";

export type LabFlagSpec = {
  key: LabFlagKey;
  labelDefault: string;
  hintDefault: string;
};

export const LAB_FLAGS: LabFlagSpec[] = [
  {
    key: "advanced_analytics_preview",
    labelDefault: "Расширенная аналитика (preview)",
    hintDefault: "Дополнительные lens-графики (PDC, ACWR, EF Tracker) когда они подъедут.",
  },
  {
    key: "experimental_charts",
    labelDefault: "Экспериментальные графики",
    hintDefault: "Новые визуализации в beta. Могут ломаться или резко меняться.",
  },
  {
    key: "voice_input_chat",
    labelDefault: "Голосовой ввод в чате",
    hintDefault: "Запись голоса в чате тренер ↔ атлет. Активно после R2 storage activation.",
  },
  {
    key: "show_beta_badges",
    labelDefault: "Показывать BETA-бейджи",
    hintDefault: "Помечает фичи в раннем доступе. Удобно отслеживать прогресс.",
  },
];

export type LabFlagsResponse = {
  flags: Partial<Record<LabFlagKey, boolean>>;
};

export function useLabFlags() {
  return useQuery<LabFlagsResponse>({
    queryKey: ["lab-flags"],
    queryFn: async () => (await api.get<LabFlagsResponse>("/athlete/profile/lab/flags")).data,
    staleTime: 60_000,
  });
}

export function useLabFlagsPatch() {
  const qc = useQueryClient();
  return useMutation<LabFlagsResponse, unknown, Partial<Record<LabFlagKey, boolean>>>({
    mutationFn: async (flags) => {
      const r = await api.patch<LabFlagsResponse>("/athlete/profile/lab/flags", { flags });
      return r.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["lab-flags"], data);
    },
  });
}
